/**
 * Thumbnail Sidebar Component
 *
 * Displays page thumbnails in a scrollable sidebar
 * Syncs with main document scroll position
 * Supports page insertion, deletion, and reordering (with FullEditing permissions)
 */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as pdfjsLib from 'pdfjs-dist';
import styles from './ThumbnailSidebar.module.scss';
import { PDFPageInfo } from './types';
import { ReactComponent as InsertIcon } from '../../shared/icons/signout-mobile-icon.svg';
import { ReactComponent as TrashIcon } from '../../shared/icons/trashcan-icon.svg';

export interface ThumbnailSidebarProps {
  pagesInfo: PDFPageInfo[];
  currentPage: number;
  onPageClick: (pageNumber: number) => void;
  pdfDocument: pdfjsLib.PDFDocumentProxy | null;
  canEditPages: boolean;
  onInsertPage?: (afterPageNumber: number, pdfFile: File) => void;
  onDeletePage?: (pageNumber: number) => void;
  onReorderPages?: (fromIndex: number, toIndex: number) => void;
}

export const ThumbnailSidebar: React.FC<ThumbnailSidebarProps> = ({
  pagesInfo,
  currentPage,
  onPageClick,
  pdfDocument,
  canEditPages,
  onInsertPage,
  onDeletePage,
  onReorderPages,
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const currentThumbnailRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [insertAfterPage, setInsertAfterPage] = useState<number | null>(null);
  const [deleteConfirmPage, setDeleteConfirmPage] = useState<number | null>(null);
  const [draggedPage, setDraggedPage] = useState<number | null>(null);
  const [dropTargetPage, setDropTargetPage] = useState<number | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(null);

  // Track ongoing render tasks to cancel them when PDF changes
  const renderTasksRef = useRef<Map<number, any>>(new Map());

  // Render thumbnails when PDF document is loaded
  useEffect(() => {
    if (!pdfDocument || pagesInfo.length === 0) return;

    let cancelled = false;

    // Cancel all ongoing render tasks when PDF changes
    renderTasksRef.current.forEach((task, pageNum) => {
      try {
        task.cancel();
      } catch (err) {
        // Ignore cancellation errors
      }
    });
    renderTasksRef.current.clear();

    const renderThumbnails = async () => {
      // Sidebar is 180px wide with 10px padding each side = 160px available width
      // Let thumbnails auto-size to fit, maintaining actual page aspect ratio
      const maxThumbnailWidth = 130; // Leave margin for right padding

      for (const pageInfo of pagesInfo) {
        if (cancelled) break;

        try {
          const page = await pdfDocument.getPage(pageInfo.pageNumber);
          const canvas = document.getElementById(`thumbnail-${pageInfo.pageNumber}`) as HTMLCanvasElement;

          if (!canvas || cancelled) continue;

          const context = canvas.getContext('2d');
          if (!context) continue;

          // Get page dimensions - viewport handles rotation automatically
          const baseViewport = page.getViewport({ scale: 1.0, rotation: page.rotate });

          // Calculate scale to fit within max width
          const thumbnailScale = maxThumbnailWidth / baseViewport.width;
          const viewport = page.getViewport({ scale: thumbnailScale, rotation: page.rotate });

          // High-DPI support
          const outputScale = window.devicePixelRatio || 1;

          // Set canvas pixel dimensions (actual rendering resolution)
          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);

          // Set canvas CSS dimensions (display size) - matches viewport aspect ratio
          canvas.style.width = Math.floor(viewport.width) + 'px';
          canvas.style.height = Math.floor(viewport.height) + 'px';

          // Create viewport at FINAL render scale to prevent double-scaling bug
          // CRITICAL: Same fix as PDFRenderer - viewport includes devicePixelRatio scaling
          const renderViewport = page.getViewport({
            scale: thumbnailScale * outputScale,
            rotation: page.rotate
          });

          // Clear canvas before rendering
          context.clearRect(0, 0, canvas.width, canvas.height);

          // Cancel any existing render task for this page
          const existingTask = renderTasksRef.current.get(pageInfo.pageNumber);
          if (existingTask) {
            try {
              existingTask.cancel();
            } catch (err) {
              // Ignore cancellation errors
            }
          }

          // Start new render task and track it
          const renderTask = page.render({
            canvasContext: context,
            viewport: renderViewport,
            transform: null,
          } as any);

          renderTasksRef.current.set(pageInfo.pageNumber, renderTask);

          // Wait for render to complete
          await renderTask.promise;

          // Remove completed task from tracking
          renderTasksRef.current.delete(pageInfo.pageNumber);
        } catch (err: any) {
          // Ignore cancellation errors, log others
          if (err?.name !== 'RenderingCancelledException') {
            console.error(`Failed to render thumbnail for page ${pageInfo.pageNumber}:`, err);
          }
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const frameId = requestAnimationFrame(() => {
      if (!cancelled) renderThumbnails();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      // Cancel all ongoing renders on cleanup
      renderTasksRef.current.forEach((task) => {
        try {
          task.cancel();
        } catch (err) {
          // Ignore cancellation errors
        }
      });
      renderTasksRef.current.clear();
    };
  }, [pdfDocument, pagesInfo]);

  // Scroll sidebar to show current page thumbnail
  useEffect(() => {
    if (currentThumbnailRef.current && sidebarRef.current) {
      const thumbnail = currentThumbnailRef.current;
      const sidebar = sidebarRef.current;

      const thumbnailTop = thumbnail.offsetTop;
      const thumbnailHeight = thumbnail.offsetHeight;
      const sidebarScrollTop = sidebar.scrollTop;
      const sidebarHeight = sidebar.clientHeight;

      // Check if thumbnail is outside visible area
      if (thumbnailTop < sidebarScrollTop || thumbnailTop + thumbnailHeight > sidebarScrollTop + sidebarHeight) {
        // Scroll to center the thumbnail
        sidebar.scrollTo({
          top: thumbnailTop - sidebarHeight / 2 + thumbnailHeight / 2,
          behavior: 'smooth',
        });
      }
    }
  }, [currentPage]);

  // Prevent scroll events from bubbling to main document container
  // One-way sync: main doc scrolling updates thumbnails, but thumbnail scrolling stays local
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = sidebar;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight;

      // Only stop propagation if we can scroll in the direction being scrolled
      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;

      if ((scrollingUp && !isAtTop) || (scrollingDown && !isAtBottom)) {
        e.stopPropagation();
      }
    };

    sidebar.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      sidebar.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Handle insert page button click
  const handleInsertClick = (e: React.MouseEvent, pageNumber: number) => {
    e.stopPropagation(); // Don't trigger page click
    setInsertAfterPage(pageNumber);
    fileInputRef.current?.click();
  };

  // Handle file selection for insert
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && insertAfterPage !== null && onInsertPage) {
      onInsertPage(insertAfterPage, file);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setInsertAfterPage(null);
  };

  // Handle delete page button click
  const handleDeleteClick = (e: React.MouseEvent, pageNumber: number) => {
    e.stopPropagation(); // Don't trigger page click
    setDeleteConfirmPage(pageNumber);
  };

  // Confirm page deletion
  const handleConfirmDelete = () => {
    if (deleteConfirmPage !== null && onDeletePage) {
      onDeletePage(deleteConfirmPage);
    }
    setDeleteConfirmPage(null);
  };

  // Cancel page deletion
  const handleCancelDelete = () => {
    setDeleteConfirmPage(null);
  };

  // Drag and drop handlers for page reordering
  const handleDragStart = (e: React.DragEvent, pageNumber: number) => {
    setDraggedPage(pageNumber);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', pageNumber.toString());
  };

  const handleDragOver = (e: React.DragEvent, pageNumber: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedPage !== null && draggedPage !== pageNumber) {
      // Determine if we're dropping before or after this page
      // Get the mouse position relative to the thumbnail
      const thumbnail = e.currentTarget as HTMLElement;
      const rect = thumbnail.getBoundingClientRect();
      const mouseY = e.clientY - rect.top;
      const thumbnailHeight = rect.height;

      // If mouse is in top half, drop before; if bottom half, drop after
      const position = mouseY < thumbnailHeight / 2 ? 'before' : 'after';

      setDropTargetPage(pageNumber);
      setDropPosition(position);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're actually leaving the thumbnail (not entering a child element)
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setDropTargetPage(null);
      setDropPosition(null);
    }
  };

  const handleDrop = (e: React.DragEvent, pageNumber: number) => {
    e.preventDefault();

    if (draggedPage !== null && draggedPage !== pageNumber && onReorderPages && dropPosition) {
      // Calculate the actual target page based on drop position
      let targetPage = pageNumber;

      // If dropping after the current page, and the dragged page is before this page,
      // we want to insert after
      if (dropPosition === 'before') {
        // If dragging down (draggedPage < pageNumber), insert before means pageNumber - 1
        // If dragging up (draggedPage > pageNumber), insert before means pageNumber
        targetPage = draggedPage < pageNumber ? pageNumber - 1 : pageNumber;
      } else {
        // Dropping after
        targetPage = draggedPage < pageNumber ? pageNumber : pageNumber + 1;
      }

      // Don't reorder if target is same as source or adjacent
      if (targetPage !== draggedPage && Math.abs(targetPage - draggedPage) !== 0) {
        onReorderPages(draggedPage, targetPage);
      }
    }

    setDraggedPage(null);
    setDropTargetPage(null);
    setDropPosition(null);
  };

  const handleDragEnd = () => {
    setDraggedPage(null);
    setDropTargetPage(null);
    setDropPosition(null);
  };

  return (
    <div className={styles.sidebar} ref={sidebarRef}>
      <div className={styles.thumbnailsContainer}>
        {pagesInfo.map((pageInfo, index) => (
          <div
            key={`page-${index}-${pageInfo.pageNumber}`}
            ref={pageInfo.pageNumber === currentPage ? currentThumbnailRef : null}
            className={`${styles.thumbnail} ${pageInfo.pageNumber === currentPage ? styles.active : ''} ${
              draggedPage === pageInfo.pageNumber ? styles.dragging : ''
            }`}
            onClick={() => onPageClick(pageInfo.pageNumber)}
            draggable={canEditPages}
            onDragStart={(e) => canEditPages && handleDragStart(e, pageInfo.pageNumber)}
            onDragOver={(e) => canEditPages && handleDragOver(e, pageInfo.pageNumber)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => canEditPages && handleDrop(e, pageInfo.pageNumber)}
            onDragEnd={handleDragEnd}
          >
            {/* Drop indicator line - shows before or after this page */}
            {dropTargetPage === pageInfo.pageNumber && dropPosition === 'before' && (
              <div className={styles.dropIndicatorBefore}></div>
            )}
            {dropTargetPage === pageInfo.pageNumber && dropPosition === 'after' && (
              <div className={styles.dropIndicatorAfter}></div>
            )}

            <div className={styles.thumbnailCanvas}>
              <canvas
                id={`thumbnail-${pageInfo.pageNumber}`}
                className={styles.canvas}
              />
            </div>
            <div className={styles.pageNumber}>
              {pageInfo.pageNumber}
            </div>

            {/* Page action icons - only show if user has edit permissions */}
            {canEditPages && (
              <div className={styles.pageActions}>
                <button
                  className={styles.actionButton}
                  onClick={(e) => handleInsertClick(e, pageInfo.pageNumber)}
                  title="Insert page after this page"
                >
                  <InsertIcon className={styles.actionIcon} />
                </button>
                <button
                  className={styles.actionButton}
                  onClick={(e) => handleDeleteClick(e, pageInfo.pageNumber)}
                  title="Delete this page"
                >
                  <TrashIcon className={styles.actionIcon} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Hidden file input for page insertion */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Delete confirmation modal - rendered via portal to escape overflow context */}
      {deleteConfirmPage !== null && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={handleCancelDelete}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Delete Page {deleteConfirmPage}</h3>
              <button className={styles.modalCloseButton} onClick={handleCancelDelete}>
                ✕
              </button>
            </div>
            <div className={styles.modalContent}>
              <p>Are you sure you want to delete page {deleteConfirmPage}?</p>
              <p className={styles.modalWarning}>This action cannot be undone.</p>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancelButton} onClick={handleCancelDelete}>
                Cancel
              </button>
              <button className={styles.modalDeleteButton} onClick={handleConfirmDelete}>
                Delete Page
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
